import React, { useMemo } from "react";
import { Table, Tag } from "antd";
import { useAppDispatch } from "../../hooks/reduxHook";
import { useListTrapSitesQuery } from "../../actions/trapsiteInQuery";
import { useListDevicesQuery } from "../../actions/devicesInQuery";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import "./index.css";
import { setWifiModalOpen, selectDevice } from "../../reducers/devicesSlice";
import { Device } from "../../types";

interface DeviceTableRecord {
  key: string;
  deviceName: string;
  location: string;
  address: string;
  coordinates: string;
  wifiConnection: string;
  status: "Online" | "Offline";
}

const TrapTable: React.FC<{}> = () => {
  const dispatch = useAppDispatch();

  const { data: trapSiteData, isLoading: isTrapSitesLoading } =
    useListTrapSitesQuery();
  const { data: devices, isLoading: isDevicesLoading } = useListDevicesQuery();

  const deviceData = useMemo((): DeviceTableRecord[] => {
    if (!devices?.items || !trapSiteData?.items) return [];

    const trapSiteMap = new Map(trapSiteData.items.map((t) => [t._id, t]));

    return devices.items.map((device) => {
      const trapSite = trapSiteMap.get(device.trap_site_id);

      return {
        key: device.uuid || "",
        deviceName: device.display_name || "N/A",
        location: trapSite?.display_name || "N/A",
        address: trapSite?.address || "N/A",
        coordinates: device?.gps
          ? `${device.gps.lat.toFixed(4)}, ${device.gps.lon.toFixed(4)}`
          : "N/A",
        wifiConnection: device.wifi || "Connect",
        status:
          (device?.status || "offline").toLowerCase() === "online"
            ? "Online"
            : "Offline",
      };
    });
  }, [devices, trapSiteData]);

  const sortIcon = useMemo(
    () =>
      ({ sortOrder }: { sortOrder: "ascend" | "descend" | null }) => {
        if (sortOrder === "ascend")
          return <ChevronUpIcon className="w-5 h-5 text-[#003A55] ml-1" />;
        if (sortOrder === "descend")
          return <ChevronDownIcon className="w-5 h-5 text-[#003A55] ml-1" />;
        return (
          <ChevronDownIcon className="w-5 h-5 opacity-0 group-hover:opacity-100 ml-1" />
        );
      },
    [],
  );

  const columns = useMemo(
    () => [
      {
        title: "Trap ID",
        dataIndex: "deviceName",
        key: "deviceName",
        sorter: (a: DeviceTableRecord, b: DeviceTableRecord) =>
          (a.deviceName || "").localeCompare(b.deviceName || ""),
        sortIcon,
        render: (text: string) => text,
      },
      {
        title: "Location",
        dataIndex: "location",
        key: "location",
        sorter: (a: DeviceTableRecord, b: DeviceTableRecord) =>
          a.location.localeCompare(b.location),
        sortIcon,
        render: (text: string) => text,
      },
      {
        title: "Address",
        dataIndex: "address",
        key: "address",
        sorter: (a: DeviceTableRecord, b: DeviceTableRecord) =>
          a.address.localeCompare(b.address),
        sortIcon,
        render: (text: string) => text,
      },
      {
        title: "Coordinates",
        dataIndex: "coordinates",
        key: "coordinates",
        sorter: (a: DeviceTableRecord, b: DeviceTableRecord) =>
          a.coordinates.localeCompare(b.coordinates),
        sortIcon,
        render: (text: string) => text,
      },
      {
        title: "Wifi connection",
        dataIndex: "wifiConnection",
        key: "wifiConnection",
        sorter: (a: DeviceTableRecord, b: DeviceTableRecord) =>
          a.wifiConnection.localeCompare(b.wifiConnection),
        sortIcon,
        render: (text: string, record: DeviceTableRecord) => (
          <span
            className="text-[#0EA5E9] cursor-pointer"
            onClick={() => {
              const device = devices?.items.find((d) => d.uuid === record.key);
              if (device) {
                dispatch(selectDevice(device as Device));
                dispatch(setWifiModalOpen(true));
              }
            }}
          >
            {text}
          </span>
        ),
      },
      {
        title: "Device",
        key: "status",
        dataIndex: "status",
        sorter: (a: DeviceTableRecord, b: DeviceTableRecord) =>
          a.status.localeCompare(b.status),
        sortIcon,
        render: (status: string) => (
          <Tag
            className={`table-device-status-tag ${status === "Online" ? "online" : "offline"}`}
          >
            {status}
          </Tag>
        ),
      },
    ],
    [devices, dispatch, sortIcon],
  );

  return (
    <div className="bg-white p-6 rounded-lg border border-[#0C4A6E]">
      <Table<DeviceTableRecord>
        columns={columns}
        dataSource={deviceData}
        loading={isTrapSitesLoading || isDevicesLoading}
        pagination={false}
        className="device-table"
      />
    </div>
  );
};

export default TrapTable;
